import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { environment } from 'src/environments/environment';
import { TabCoordinatorService } from './tab-coordinator.service';

const STORAGE_KEY = 'gr_time_buffer';

const INACTIVITY_TIMEOUT_MS = 5 * 60_000;    // 5min sin actividad → pausar contador
const LONG_INACTIVITY_FLUSH_MS = 30 * 60_000; // 30min sin actividad → flush
const SAFETY_NET_MS = 15 * 60_000;            // flush cada 15 min como red de seguridad
const ACTIVITY_THROTTLE_MS = 200;             // throttle para mousemove/scroll

@Injectable({ providedIn: 'root' })
export class ScreenTimeTrackerService {
  private isVisible = true;
  private isActive = true;
  private accumulatedSeconds = 0;
  private lastActivityTime = 0;

  private counterInterval: any;
  private safetyNetInterval: any;
  private inactivityTimer: any;
  private longInactivityTimer: any;
  private running = false;

  // Stored refs for cleanup in stop()
  private readonly visibilityHandler = () => {
    this.isVisible = document.visibilityState === 'visible';
  };
  private readonly activityHandler = () => this.onUserActivity();
  private readonly beforeUnloadHandler = () => {
    const seconds = this.accumulatedSeconds;
    if (seconds <= 0) return;

    if (this.tabCoordinator.isMaster) {
      this.sendBeaconToBackend(seconds);
    } else {
      this.tabCoordinator.reportToMaster(seconds);
    }
    this.saveToStorage(0);
  };

  constructor(
    private tabCoordinator: TabCoordinatorService,
    private cookieService: CookieService
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;

    // Restore any unsent seconds from a previous session
    this.accumulatedSeconds = this.getStoredSeconds();

    this.setupVisibilityTracking();
    this.setupActivityTracking();
    this.setupCounter();
    this.setupSafetyNet();
    this.setupBeforeUnload();
    this.setupTabCoordinator();
  }

  stop(): void {
    this.running = false;
    clearInterval(this.counterInterval);
    clearInterval(this.safetyNetInterval);
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.longInactivityTimer);

    // Remove all event listeners to avoid accumulation on re-login
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach(event => window.removeEventListener(event, this.activityHandler));
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /** Call this on logout: flush remaining time and stop */
  async flushAndStop(): Promise<void> {
    await this.flush();
    this.stop();
  }

  // ---------------------------------------------------------------------------
  // Setup methods
  // ---------------------------------------------------------------------------

  private setupVisibilityTracking(): void {
    this.isVisible = document.visibilityState === 'visible';
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private setupActivityTracking(): void {
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach(event =>
      window.addEventListener(event, this.activityHandler, { passive: true })
    );
    // Start the inactivity timer immediately
    this.onUserActivity();
  }

  private setupCounter(): void {
    this.counterInterval = setInterval(() => {
      if (this.isVisible && this.isActive) {
        this.accumulatedSeconds++;
        this.saveToStorage(this.accumulatedSeconds);
      }
    }, 1000);
  }

  private setupSafetyNet(): void {
    this.safetyNetInterval = setInterval(() => this.flush(), SAFETY_NET_MS);
  }

  private setupBeforeUnload(): void {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private setupTabCoordinator(): void {
    // When master receives time from a slave, add it to its own accumulated total
    this.tabCoordinator.onSlaveTime = (seconds: number) => {
      this.accumulatedSeconds += seconds;
    };

    this.tabCoordinator.init();
  }

  // ---------------------------------------------------------------------------
  // Activity handling
  // ---------------------------------------------------------------------------

  private onUserActivity(): void {
    const now = Date.now();
    if (now - this.lastActivityTime < ACTIVITY_THROTTLE_MS) return;
    this.lastActivityTime = now;

    this.isActive = true;
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.longInactivityTimer);

    this.inactivityTimer = setTimeout(() => {
      this.isActive = false;
    }, INACTIVITY_TIMEOUT_MS);

    this.longInactivityTimer = setTimeout(() => {
      this.flush();
    }, LONG_INACTIVITY_FLUSH_MS);
  }

  // ---------------------------------------------------------------------------
  // Flush logic
  // ---------------------------------------------------------------------------

  private async flush(): Promise<void> {
    const seconds = this.accumulatedSeconds;
    if (seconds <= 0) return;

    this.accumulatedSeconds = 0;
    this.saveToStorage(0);

    if (this.tabCoordinator.isMaster) {
      await this.sendToBackend(seconds);
    } else {
      this.tabCoordinator.reportToMaster(seconds);
    }
  }

  private async sendToBackend(seconds: number): Promise<void> {
    const token = this.cookieService.get('gr_access');
    const url = `${environment.apiUrl}/users/track-time`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seconds_active: seconds }),
      });
    } catch {
      // Network error: restore seconds so they are not lost
      this.accumulatedSeconds += seconds;
      this.saveToStorage(this.accumulatedSeconds);
    }
  }

  /** Uses fetch keepalive (like sendBeacon but supports JSON + auth headers) */
  private sendBeaconToBackend(seconds: number): void {
    const token = this.cookieService.get('gr_access');
    const url = `${environment.apiUrl}/users/track-time`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ seconds_active: seconds }),
      keepalive: true,
    }).catch(() => {});
  }

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  private saveToStorage(seconds: number): void {
    localStorage.setItem(STORAGE_KEY, String(seconds));
  }

  private getStoredSeconds(): number {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  }
}
